/**
 * A thing this portal does not have — an address, or a row.
 *
 * One line and a way back, which is DESIGN.md P7's empty state applied to a page:
 * "Empty states are **one line**, not a bordered panel with an icon and a
 * three-sentence paragraph."
 *
 * It is the router's `notFoundComponent` **and** what `RouteError` renders for a
 * `404` problem body, which is R1-25: `/app/nope` already kept the shell and said
 * "404 · There is nothing here · Back to the portal", while `/app/reports/2099-01`
 * — a month with no report, which is the same sentence about a row instead of a
 * route — showed a machine code, a request id and a *Try again* that could never
 * do anything, because a 404 is not transient. A missing thing is a missing thing;
 * there is one card for it.
 *
 * `detail` is the only difference between the two callers. An unmatched route has
 * nothing to add, so it gets the sentence below; a problem body carries the one
 * `http/problem.ts` wrote for the caller ("No report exists for January 2099"),
 * which is better than anything this file could guess.
 */

import { BackToPortal, ErrorCard } from './ErrorCard.js'

/** What an address that matches no route has to say for itself. */
export const NO_SUCH_PAGE = 'That address does not match a page in this portal.'

export type NotFoundProps = {
  /** The server's own sentence, when this came from a `404` problem body. */
  readonly detail?: string | undefined
  /** `instance` from that body. An attribute for a developer, never a sentence. */
  readonly requestId?: string | null | undefined
  /**
   * Whatever `notFound({ data })` carried, which is nothing anywhere in this app —
   * and is declared only so the component still satisfies TanStack Router's
   * `notFoundComponent`. Without it the two prop types share no member, which is a
   * *weak type* mismatch and a compile error at every `notFoundComponent={NotFound}`.
   */
  readonly data?: unknown
}

export function NotFound({ detail, requestId }: NotFoundProps = {}) {
  return (
    <ErrorCard
      cap="404"
      title="There is nothing here"
      detail={detail === undefined || detail === '' ? NO_SUCH_PAGE : detail}
      requestId={requestId}
    >
      <BackToPortal />
    </ErrorCard>
  )
}
